import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Seo from '../../seo/Seo';
import { websiteSchema } from '../../seo/schema';
import Icon from './Icon';
import ModuleRiver from './ModuleRiver';
import WorkflowShowcase from './WorkflowShowcase';
import {
  RAIL_ITEMS, HUB_TILES, STATEMENTS, MODULES_GRID, PRODUCT_PAGES,
  COMPLIANCE_CHIPS, HOW_STEPS, INDUSTRIES, SECURITY, FAQS,
} from './homeData';
import { softwareApplicationSchema } from '../pricing/pricingData';
import './Home.css';

// ─── Scroll-reveal: content is visible by default; JS layers a subtle entrance ─
const useRevealObserver = () => {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.rl-reveal'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); observer.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    els.forEach((el) => observer.observe(el));
    // Failsafe: never leave content hidden.
    const failsafe = setTimeout(() => els.forEach((el) => el.classList.add('in')), 1200);
    return () => { observer.disconnect(); clearTimeout(failsafe); };
  }, []);
};

// Four things Raya AI does, chosen because each maps to a job someone is doing
// by hand right now. The full story lives at /ai.
const AI_HIGHLIGHTS = [
  { icon: 'receipt-indian-rupee', title: 'Bills become vouchers', desc: 'Photograph it — AI reads the vendor, verifies the GSTIN and picks the ledger.' },
  { icon: 'landmark', title: 'Statements reconcile themselves', desc: 'AI matches each line to an invoice or bill and flags only what needs you.' },
  { icon: 'line-chart', title: 'History becomes a forecast', desc: 'What will sell, what runs short, and the date the indent has to go out.' },
  { icon: 'clipboard-check', title: 'You still sign it off', desc: 'Every draft waits for a person. Cap what AI may draft, or switch it off.' },
];

const EmailCapture = ({ ctaLabel = 'Start free' }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const submit = (e) => {
    e.preventDefault();
    navigate('/register', email ? { state: { email } } : undefined);
  };
  return (
    <form className="rl-emailcapture" onSubmit={submit}>
      <div className="rl-emailcapture-field">
        <input
          type="email"
          className="rl-emailcapture-input"
          placeholder="Your work email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Your work email"
        />
        <button type="submit" className="rl-btn rl-btn--primary">{ctaLabel}</button>
      </div>
    </form>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const faqRefs = useRef([]);
  useRevealObserver();

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => scrollToSection(location.state.scrollTo), 100);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  return (
    <div className="rl-home">
      <Seo path="/" faqs={FAQS} schema={[websiteSchema(), softwareApplicationSchema()]} />

      {/* ── Hero ─────────────────────────────── */}
      <section id="top" className="hero">
        <div className="hero-grid-bg" aria-hidden="true" />
        <div className="hero-blob hero-blob--blue" aria-hidden="true" />
        <div className="hero-blob hero-blob--mint" aria-hidden="true" />
        <div className="hero-inner">
          <a href="/ai" className="hero-badge" onClick={(e) => { e.preventDefault(); navigate('/ai'); }}>
            <span className="hero-badge-dot" />New &middot; Raya AI drafts your entries
            <span className="hero-badge-chip">See it work →</span>
          </a>
          <h1 className="hero-title">Run your whole business on <em>one AI-powered platform.</em></h1>
          <p className="hero-sub">Inventory, sales, purchase, manufacturing, finance and HR — with GST, e-Invoice and e-Way Bill built in. Photograph a bill and AI fills in the voucher.</p>
          <div className="hero-actions">
            <EmailCapture />
            <a href="#workflows" className="rl-btn rl-btn--ghost" onClick={(e) => { e.preventDefault(); scrollToSection('workflows'); }}>See a live workflow →</a>
          </div>
          <div className="hero-meta">Raya ERP · AI-powered · Built for Indian businesses</div>

          {/* Product frame */}
          <div className="hero-product">
            <div className="hero-frame">
              <div className="hero-window">
                <div className="hero-window-rail">
                  {RAIL_ITEMS.map((r, i) => (
                    <div className="hero-rail-item" key={i} style={{ color: r.color, background: r.bg }}>
                      <Icon name={r.icon} size={18} />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="hero-window-bar">
                    <span className="hero-window-org">Your Company Pvt Ltd ▾</span>
                    <div className="hero-window-search">
                      <span>Search...</span>
                      <span className="hero-window-kbd">Ctrl+K</span>
                    </div>
                    <span className="hero-window-create">+ Create</span>
                  </div>
                  <div className="hero-window-body">
                    <div className="hero-greeting">Good morning, Priya</div>
                    <div className="hero-greeting-sub">Pick a module to get started, or press Ctrl+K to jump anywhere.</div>
                    <div className="hero-alerts">
                      <span className="hero-alert hero-alert--err"><span className="hero-alert-dot" />3 items out of stock</span>
                      <span className="hero-alert hero-alert--warn"><span className="hero-alert-dot" />2 work orders due today</span>
                    </div>
                    <div className="hero-corelabel">Core business</div>
                    <div className="hero-tiles">
                      {HUB_TILES.map((t) => (
                        <div className="hero-tile" key={t.name}>
                          <div className="hero-tile-top">
                            <div className="hero-tile-icon" style={{ background: t.tint, color: t.color }}>
                              <Icon name={t.icon} size={16} />
                            </div>
                            <span className="hero-tile-chip" style={{ color: t.color, background: t.tint }}>{t.chip}</span>
                          </div>
                          <div className="hero-tile-name">{t.name}</div>
                          <div className="hero-tile-desc">{t.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="hero-float hero-float--tr">
              <div className="hero-float-icon hero-float-icon--ok">✓</div>
              <div className="hero-float-text">
                <div className="hero-float-title">e-Invoice IRN generated</div>
                <div className="hero-float-sub">INV-2043 · IRP confirmed</div>
              </div>
            </div>
            <div className="hero-float hero-float--bl">
              <div className="hero-float-icon hero-float-icon--blue"><Icon name="indian-rupee" size={15} /></div>
              <div className="hero-float-text">
                <div className="hero-float-title">₹5,68,760 received</div>
                <div className="hero-float-sub">Matched to INV-2043 · reconciled</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI band ─────────────────────────────── */}
      <section className="rl-section ai-band" id="ai">
        <div className="rl-container rl-reveal">
          <div className="ai-band-grid">
            <div className="ai-band-text">
              <div className="ai-band-eyebrow"><Icon name="sparkles" size={13} />Raya AI · works across every module</div>
              <h2 className="rl-h2">An ERP that <em>does the typing.</em></h2>
              <p className="rl-lead">
                Raya AI reads the bill on your desk, picks the right party and ledger, reconciles the
                bank statement and forecasts what to reorder — then hands you a draft to approve.
                Nothing reaches your books without a person.
              </p>
              <div className="ai-band-actions">
                <button type="button" className="rl-btn rl-btn--primary" onClick={() => navigate('/ai')}>See what Raya AI does →</button>
                <span className="ai-band-free">Assign a seat to the accounts that need one</span>
              </div>
            </div>
            <div className="ai-band-cards">
              {AI_HIGHLIGHTS.map((h) => (
                <div className="ai-band-card" key={h.title}>
                  <span className="ai-band-icon"><Icon name={h.icon} size={17} /></span>
                  <div>
                    <div className="ai-band-title">{h.title}</div>
                    <p className="ai-band-desc">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Statement band ─────────────────────────────── */}
      <section id="platform" className="rl-section">
        <div className="rl-container rl-reveal">
          <div className="rl-eyebrow">— A familiar problem</div>
          <h2 className="rl-h2 rl-h2--lg">Spreadsheets, chat threads, five logins. <em>Ready to consolidate?</em></h2>
          <p className="rl-lead">Enter data once. Every module reads and writes the same records, so stock, invoices, compliance, costing and payroll stay in step on their own.</p>
          <div className="statement-grid">
            {STATEMENTS.map((s) => (
              <div key={s.title}>
                <div className="statement-figure">{s.figure}</div>
                <div className="statement-title">{s.title}</div>
                <p className="statement-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Modules grid ─────────────────────────────── */}
      <section id="modules" className="rl-section rl-section--alt">
        <div className="rl-container rl-reveal">
          <div className="rl-eyebrow">— The modules</div>
          <h2 className="rl-h2">Everything your business runs on, <em>in one place.</em></h2>
          <div className="modules-grid">
            {MODULES_GRID.map((m) => (
              <div className="module-card" key={m.name} style={{ '--hover-border': m.color }}>
                <div className="module-card-icon" style={{ background: m.tint, color: m.color }}>
                  <Icon name={m.icon} size={20} />
                </div>
                <h3 className="module-card-name">{m.name}</h3>
                <p className="module-card-desc">{m.desc}</p>
              </div>
            ))}
          </div>
          <div className="modules-apps">
            {PRODUCT_PAGES.map((m) => (
              <a key={m.href} href={m.href} className="modules-app" style={{ '--app-color': m.color, '--app-tint': m.tint }} onClick={(e) => { e.preventDefault(); navigate(m.href); }}>
                <span className="modules-app-icon"><Icon name={m.icon} size={19} /></span>
                <span className="modules-app-text">
                  <span className="modules-app-name">{m.name}{m.tag && <span className="modules-app-tag">{m.tag}</span>}</span>
                  <span className="modules-app-desc">{m.desc}</span>
                </span>
                <Icon name="arrow-right" size={15} className="modules-app-arrow" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Module river ─────────────────────────────── */}
      <ModuleRiver />

      {/* ── Workflows ─────────────────────────────── */}
      <WorkflowShowcase />

      {/* ── Compliance ─────────────────────────────── */}
      <section id="gst" className="rl-section">
        <div className="rl-container compliance-grid rl-reveal">
          <div>
            <div className="rl-eyebrow">— Compliance</div>
            <h2 className="rl-h2">GST &amp; e-Invoice, <em>built in.</em></h2>
            <p className="rl-lead rl-lead--tight">Filing-ready from day one. No third-party plugins, no month-end scramble.</p>
            <div className="compliance-chips">
              {COMPLIANCE_CHIPS.map((c) => (
                <span className="compliance-chip" key={c}><span className="compliance-chip-dot" />{c}</span>
              ))}
            </div>
          </div>
          <div className="rl-window-frame rl-window-frame--pad">
            <div className="compliance-card">
              <div className="compliance-card-head">
                <span className="compliance-card-title">Tax Invoice — INV-2043</span>
                <span className="compliance-card-badge">✓ IRN generated</span>
              </div>
              <div className="compliance-card-body">
                <div className="compliance-card-fields">
                  <div className="compliance-field">
                    <div className="compliance-field-label">IRN</div>
                    <div className="compliance-field-mono">a4f8…c91e2b7d4f6a8c3e5d7b9f1a2c4e6d8b</div>
                  </div>
                  <div className="compliance-field">
                    <div className="compliance-field-label">e-Way Bill</div>
                    <div className="compliance-field-strong">EWB-8812 4405 7723</div>
                  </div>
                </div>
                <div className="compliance-qr"><div className="compliance-qr-inner" /></div>
              </div>
              <div className="compliance-card-pills">
                <span className="compliance-pill">Posted to GSTR-1 data</span>
                <span className="compliance-pill">HSN validated</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────── */}
      <section id="how" className="rl-section rl-section--alt">
        <div className="rl-container rl-reveal">
          <div className="rl-head rl-head--center">
            <div className="rl-eyebrow">— Getting started</div>
            <h2 className="rl-h2">Up and running <em>in minutes.</em></h2>
            <p className="rl-lead rl-lead--center">Sign up and go. Your team can be working the same day.</p>
          </div>
          <div className="how-grid">
            {HOW_STEPS.map((s) => (
              <div className="how-card" key={s.num}>
                <div className="how-num">{s.num}</div>
                <h3 className="how-title">{s.title}</h3>
                <p className="how-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ─────────────────────────────── */}
      <section id="industries" className="rl-section">
        <div className="rl-container rl-reveal">
          <div className="rl-eyebrow">— Industries</div>
          <h2 className="rl-h2">One platform, <em>tuned to your trade.</em></h2>
          <div className="industries-grid">
            {INDUSTRIES.map((ind) => (
              <div className="industry-card" key={ind.name}>
                <div className="industry-icon"><Icon name={ind.icon} size={20} /></div>
                <div>
                  <h3 className="industry-name">{ind.name}</h3>
                  <p className="industry-desc">{ind.desc}</p>
                  {ind.link && (
                    <a href={ind.link} className="industry-link" onClick={(e) => { e.preventDefault(); navigate(ind.link); }}>
                      {ind.linkLabel} <Icon name="arrow-right" size={13} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Security ─────────────────────────────── */}
      <section className="rl-section rl-section--tight">
        <div className="rl-container rl-reveal">
          <div className="security-grid">
            {SECURITY.map((s) => (
              <div className="security-card" key={s.title}>
                <div className="security-icon"><Icon name={s.icon} size={20} /></div>
                <div>
                  <h3 className="security-title">{s.title}</h3>
                  <p className="security-desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────── */}
      <section id="faq" className="rl-section rl-section--alt">
        <div className="rl-container rl-container--narrow rl-reveal">
          <div className="rl-head rl-head--center">
            <div className="rl-eyebrow">— FAQ</div>
            <h2 className="rl-h2">Common <em>questions.</em></h2>
          </div>
          <div className="faq-list">
            {FAQS.map((q, i) => (
              <details className="faq-item" key={q.q} ref={(el) => { faqRefs.current[i] = el; }}>
                <summary className="faq-q">{q.q}<span className="faq-plus">+</span></summary>
                <p className="faq-a">{q.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────── */}
      <section id="cta" className="cta-section">
        <div className="cta-blob" aria-hidden="true" />
        <div className="rl-container rl-reveal cta-inner">
          <div className="cta-headline">Ready to consolidate?</div>
          <div className="cta-actions">
            <EmailCapture />
            <button type="button" className="rl-btn rl-btn--outline" onClick={() => navigate('/contact-us')}>Talk to us</button>
          </div>
          <div className="cta-meta">— Try Raya ERP at <span className="cta-domain">erp.rayasolutions</span><em>.in</em></div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
